import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SectionLabel } from './ui/UI';
import { BlurText } from './animations/BlurText';
import { ShinyText } from './animations/ShinyText';
const SEAN_PORTRAIT = 'https://cdn.magicpatterns.com/uploads/kB2BfftmcjPffkK7NuximY/Screenshot_2025-02-21_at_6.47.22_PM.png';
const ventures = [{
  name: 'Periscan',
  detail: 'Founder · 2004 / 2026'
}, {
  name: 'Barracuda',
  detail: 'First PM · VP Strategy'
}, {
  name: 'SignalWire',
  detail: 'Co-Founder · CMO · COO'
}, {
  name: 'Advisory',
  detail: 'Cyber · AI · CPaaS'
}];
export function FounderCard() {
  return <section id="founder-card" className="relative w-full py-32 border-t border-white/5" data-id="element-549">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-550">
        <SectionLabel number="—" label="Founder" className="mb-8" data-id="element-551" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-2xl overflow-hidden border border-white/10 bg-[#070a0e]" data-id="element-552">
          {/* Portrait */}
          <div className="lg:col-span-4 relative bg-black" data-id="element-553">
            <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full min-h-[360px]" data-id="element-554">
              <img src={SEAN_PORTRAIT} alt="Sean Heiney, Founder & CEO of Periscan" className="absolute inset-0 w-full h-full object-contain object-center" data-id="element-555" />
              <div className="absolute inset-0 pointer-events-none" style={{
              background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.7) 100%)'
            }} data-id="element-556" />
              <div className="absolute top-5 right-5 text-right" data-id="element-557">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80" data-id="element-558">
                  Founder · 2026
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5" data-id="element-559">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4] mb-1" data-id="element-560">
                  Founder & CEO
                </p>
                <p className="text-xl font-semibold text-white tracking-tight" data-id="element-561">
                  Sean Heiney
                </p>
              </div>
            </div>
          </div>

          {/* Copy */}
          <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-between gap-8" data-id="element-562">
            <div data-id="element-563">
              <h3 className="text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mb-6" data-id="element-564">
                A security validation pioneer,
                <br data-id="element-565" />
                <span className="text-white/40" data-id="element-566">rebuilt for now.</span>
              </h3>
              <div className="space-y-4 max-w-xl" data-id="element-567">
                <p className="text-base text-white/70 leading-relaxed" data-id="element-568">
                  Periscan helped define an earlier generation of SaaS-managed
                  security validation, before the market had language for ASV,
                  AEV, CTEM, BAS, EASM, or exposure validation.
                </p>
                <p className="text-base text-white/70 leading-relaxed" data-id="element-569">
                  Founder Sean Heiney has since built and scaled companies
                  including{' '}
                  <span className="text-white" data-id="element-570">Barracuda Networks</span> (first
                  PM → VP Strategy, products to $200M+ ARR) and{' '}
                  <span className="text-white" data-id="element-571">SignalWire</span> (co-founder,
                  CMO, COO, $42M+ raised).
                </p>
                <p className="text-base text-white/85 leading-relaxed pt-2" data-id="element-572">
                  Now Periscan returns for a world where cloud infrastructure,
                  identity, SaaS, detection rules, and attacker techniques
                  change constantly.
                </p>
                <p className="text-base text-white leading-relaxed" data-id="element-573">
                  Same basic idea: prove what works.
                </p>
                <p className="font-mono text-sm text-[#1a8fd4]" data-id="element-574">
                  Much less patience for security theater.
                </p>
              </div>
            </div>

            {/* Ventures rail */}
            <div data-id="element-575">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3" data-id="element-576">
                Selected ventures
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-577">
                {ventures.map(v => <div key={v.name} className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]" data-id="element-578">
                    <p className="text-sm font-semibold text-white" data-id="element-579">{v.name}</p>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-0.5" data-id="element-580">
                      {v.detail}
                    </p>
                  </div>)}
              </div>

              <Link to="/about#founder" className="group mt-6 inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#1a8fd4] transition-colors" data-id="element-581">
                Read the full founder story
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-582" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}